/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import allActions from '../actions';
import { requestlogout, storeStudyPath } from '../actions/AuthActions';
import { logout } from '../auth/authService';
import useServerConfig, { getAvailableServers, RAIMV7_SERVER_TYPE } from '../util/useServerConfig';
import { getStudyList } from './studyLobbyUtils';
import StudiesHeader from './StudiesHeader';
import { StudiesTable } from './StudiesTable';
import { TablePagination } from './TablePagination';
import useDebounce from '../util/useDebounce';
import { StyledFooter, StyledTableWrapper } from './StyledTable';
import { encrypt } from '../auth/service/cryptEncode';
import { getSessionId } from '../util/Session';
import { LoginData } from '../util/LoginData';

const HOURS_IN_A_MONTH = 730;

const initializeSearchFilters = (searchFiltersConfig, initialTimespan) => {
  const initialFilters = {};
  searchFiltersConfig.forEach((filter) => initialFilters[filter.qidoQueryStringParam] = '');

  if (initialTimespan !== -1) {
    const studyDateFrom = moment().subtract(initialTimespan || 36, 'hours');
    const studyDateTo = moment();

    Object.assign(initialFilters, { ...initialFilters, studyDateFrom, studyDateTo });
  }

  return initialFilters;
};

export const StudyLobby = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const server = useServerConfig(true);
  const appConfig = useSelector((state) => state.config);
  const { WITH_LOGIN, WITH_ACCESSION_NUMBER_LOADING, WITH_HIBA_URL_ENCRYPTED, HIBA_SALT, SEARCH_INITIAL_TIMESPAN_HOURS, SEARCH_WITH_BUTTON,
    SEARCH_TOTAL_STUDIES, PACS_WITHOUT_WILDCARD = false } = appConfig;
  const manager = useSelector((state) => state.manager);
  const { studiesList: studies, currentPage, rowsPerPage, filters: currentFilters, selectedPacsIndex } = manager;
  const searchRequestId = useRef(0);
  const [prevPage, setPrevPage] = useState(currentPage);
  const { authenticated, logout: sessionLogout } = LoginData();

  // ~~ STATE
  const [searchStatus, setSearchStatus] = useState({
    isSearchingForStudies: false,
    error: null,
  });
  const [sort, setSort] = useState({
    fieldName: 'StudyDate',
    direction: 'asc',
  });

  const studySearchConfig = useSelector((state) => state.manager.studySearchConfig);
  const searchFiltersConfig = studySearchConfig && studySearchConfig.searchFilters ? studySearchConfig.searchFilters : [];
  const userFilters = studySearchConfig && studySearchConfig.userFilters ? studySearchConfig.userFilters : [];
  const searchTimeRange = SEARCH_INITIAL_TIMESPAN_HOURS || HOURS_IN_A_MONTH;

  const searchFiltersKey = `searchFilters_${getSessionId()}`;
  const initialFilters = initializeSearchFilters(searchFiltersConfig, searchTimeRange);

  const [filters, setFilters] = useState(() => {
    const storedFiltersJSON = sessionStorage.getItem(searchFiltersKey);
    const storedFilters = JSON.parse(storedFiltersJSON, (key, value) => {
      if ((key === 'studyDateFrom' || key === 'studyDateTo') && value !== null) {
        // Parse the serialized moment object back into a moment object
        return moment(value);
      }
      return value;
    });
    return storedFilters || initialFilters;
  });

  useEffect(() => {
    sessionStorage.setItem(searchFiltersKey, JSON.stringify(filters));
  }, [filters]);

  const debouncedSort = useDebounce(sort, 200);
  let debouncedFilters;

  if (!SEARCH_WITH_BUTTON) {
    debouncedFilters = useDebounce(filters, 700);
  }

  const handleClick = (AccessionNumber, StudyInstanceUID, seriesInstanceUIDs = []) => {
    const accNum = WITH_HIBA_URL_ENCRYPTED ? encrypt(AccessionNumber, HIBA_SALT) : AccessionNumber;
    const studyPath = WITH_ACCESSION_NUMBER_LOADING ? `/viewer/access/${accNum}` : `/viewer/study/${StudyInstanceUID}`;
    const seriesPath = seriesInstanceUIDs.length ? `/series/${seriesInstanceUIDs.join(',')}` : '';
    history.push(`${studyPath}${seriesPath}`);
  };

  const handleSearchButtonClick = () => {
    searchStudies();
  };

  const handleSort = (fieldName) => {
    let sortFieldName = fieldName;
    let sortDirection = 'asc';

    if (fieldName === sort.fieldName) {
      if (sort.direction === 'asc') {
        sortDirection = 'desc';
      } else {
        sortFieldName = null;
        sortDirection = null;
      }
    }

    setSort({
      fieldName: sortFieldName,
      direction: sortDirection,
    });
  };

  const handleFilterChange = (fieldName, value) => {
    dispatch(allActions.Manager.setCurrentPage(0));
    setFilters((state) => ({
      ...state,
      [fieldName]: value,
    }));
  };

  // const handleLogout = () => {
  //   dispatch(requestlogout());
  //   dispatch(storeStudyPath('/'));
  //   logout({ history });
  // };

  /**
   * Asynchronous function to search studies based on specified filters and pagination parameters.
   * Sets search status and dispatches search results to the store.
   */
  const searchStudies = async () => {
    const requestId = ++searchRequestId.current;
    const isCurrentRequest = () => requestId === searchRequestId.current;
    setSearchStatus({ error: null, isSearchingForStudies: true });

    const availableServers = getAvailableServers(server, true);

    const handleAdditionalFetches = async (server, studyListResponse) => {
      const nextPageStudyList = await getStudyList(server, filters, userFilters, rowsPerPage, currentPage + 1, sort, PACS_WITHOUT_WILDCARD);
      if (!isCurrentRequest()) return;
      const isNextPageAvailable = !!nextPageStudyList.length;
        let totalStudies = 0;
      let isRaim = false;

      if (SEARCH_TOTAL_STUDIES) {
          isRaim = !Array.isArray(server.wadoUriRoot) ? server.wadoUriRoot.includes(RAIMV7_SERVER_TYPE) : server.wadoUriRoot[0].includes(RAIMV7_SERVER_TYPE);
            const thousandStudiesHeaders = appConfig.authorizationHeader ? { headers: { Authorization: appConfig.authorizationHeader } } : {};

        if (!isRaim) {
          const fetched = await fetch(`${server.qidoRoot}/studies?limit=1000`, thousandStudiesHeaders).then((response) => (response instanceof Object ? response : response));
          const thousandStudies = await fetched.json().then((res) => res);
          if (!isCurrentRequest()) return;
          totalStudies = thousandStudies.length === 1000 ? '+1000' : thousandStudies.length;
        }
        }

      dispatch(
        allActions.Manager.setSearch({
          studiesList: studyListResponse,
          searchFiltersConfig,
          totalStudies,
          isRaim: SEARCH_TOTAL_STUDIES ? isRaim : undefined,
          isNextPageAvailable,
        })
      );
    };

    const initialFetchPromises = availableServers.map(async (server) => {
      const studyListResponse = await getStudyList(server, filters, userFilters, rowsPerPage, currentPage, sort, PACS_WITHOUT_WILDCARD);

      if (isCurrentRequest() && studyListResponse) {
        dispatch(
          allActions.Manager.setSearch({
            studiesList: studyListResponse,
            searchFiltersConfig,
          })
        );

        handleAdditionalFetches(server, studyListResponse);
      }
    });

    await Promise.all(initialFetchPromises);

    if (isCurrentRequest()) setSearchStatus({ error: null, isSearchingForStudies: false });
  };

  useEffect(() => {
    searchStudies();
  }, [debouncedFilters, debouncedSort, rowsPerPage, currentPage, selectedPacsIndex]);

  return (
    <StyledTableWrapper>
      <div className={isMobile ? 'mobile' : 'desktop'}>
        <StudiesHeader />
        {searchStatus && (
          <StudiesTable
            sort={sort}
            onSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
            studies={studies}
            handleClick={handleClick}
            handleSearchButtonClick={handleSearchButtonClick}
            seriesServers={getAvailableServers(server, true)}
          />
        )}
        {searchStatus && (
          <TablePagination setPrevPage={setPrevPage} />
        )}
        <StyledFooter>
          {authenticated && (
            <button type="submit" className="btn btn-primary m-3 ml-auto" onClick={sessionLogout}>
              {t('logout')}
            </button>
          )}
        </StyledFooter>
      </div>
    </StyledTableWrapper>
  );
};
